/*
 * Load more / lazy-loading voor collectiepagina's (progressive enhancement).
 *
 * - Rendert een echte <a href="?page=N" rel="next"> die zonder JS gewoon werkt.
 * - Met JS: laadt de volgende pagina's automatisch (IntersectionObserver) bij tot ~100
 *   producten zichtbaar zijn, daarna verschijnt een handmatige "Meer producten laden"-knop.
 * - Haalt volgende pagina's op via de Shopify Section Rendering API en voegt de nieuwe
 *   producten toe aan het bestaande grid.
 * - Werkt de URL bij met history.pushState(?page=N) en integreert met de bestaande
 *   filter-/sorteerlogica (facets.js): bij een filterwijziging wordt de sectie opnieuw
 *   gerenderd (pagina 1) waardoor dit element opnieuw wordt geïnitialiseerd.
 */
if (!customElements.get('load-more-products')) {
  customElements.define(
    'load-more-products',
    class LoadMoreProducts extends HTMLElement {
      connectedCallback() {
        this.grid = document.getElementById('product-grid');
        this.button = this.querySelector('[data-load-more-button]');
        this.sentinel = this.querySelector('[data-load-more-sentinel]');
        this.spinner = this.querySelector('.load-more__spinner');
        this.progressBar = this.querySelector('.load-more__progress-bar');
        this.statusShown = this.querySelector('.load-more__status-shown');
        this.autoLimit = parseInt(this.dataset.autoLimit || '100', 10);
        this.total = parseInt(this.dataset.total || '0', 10);
        this.loading = false;

        if (!this.grid) return;

        this.sectionId = this.grid.dataset.id;

        // Markeer dat JS actief is → verbergt de genummerde (SEO) paginering via CSS.
        this.classList.add('load-more--enhanced');

        if (this.button) {
          this.button.addEventListener('click', this.onButtonClick.bind(this));
        }

        this.setupObserver();
        this.updateMode();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      get renderedCount() {
        return this.grid.querySelectorAll(':scope > li').length;
      }

      setupObserver() {
        if (!('IntersectionObserver' in window) || !this.sentinel) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !this.loading) this.loadNext(false);
            });
          },
          { rootMargin: '600px 0px' }
        );
      }

      // Bepaalt of we in de automatische fase zitten (< autoLimit) of de handmatige knop tonen.
      updateMode() {
        if (!this.button) return;

        const isAutoPhase = this.renderedCount < this.autoLimit;

        if (isAutoPhase && this.observer && this.sentinel) {
          this.button.classList.add('load-more__button--hidden');
          // Opnieuw observeren forceert een verse check: als de sentinel nog in beeld is
          // (grid korter dan viewport) wordt direct de volgende batch geladen.
          this.observer.unobserve(this.sentinel);
          this.observer.observe(this.sentinel);
        } else {
          if (this.observer && this.sentinel) this.observer.unobserve(this.sentinel);
          this.button.classList.remove('load-more__button--hidden');
        }
      }

      onButtonClick(event) {
        event.preventDefault();
        this.loadNext(true);
      }

      async loadNext(manual) {
        if (this.loading || !this.button) return;

        const nextUrl = this.button.getAttribute('href');
        if (!nextUrl) return;

        this.loading = true;
        this.setBusy(true);

        try {
          const fetchUrl = new URL(nextUrl, window.location.origin);
          if (this.sectionId) fetchUrl.searchParams.set('section_id', this.sectionId);

          const response = await fetch(fetchUrl.toString());
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const text = await response.text();
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const newGrid = doc.getElementById('product-grid');
          const newItems = newGrid ? Array.from(newGrid.querySelectorAll(':scope > li')) : [];

          let firstAppended = null;
          newItems.forEach((item, index) => {
            const node = document.importNode(item, true);
            // Voorkom dat reveal-on-scroll animaties de nieuwe items verborgen houden.
            node.classList.add('scroll-trigger--cancel');
            if (index === 0) firstAppended = node;
            this.grid.appendChild(node);
          });

          // URL bijwerken zodat back-knop en delen blijven werken.
          const stateUrl = new URL(nextUrl, window.location.origin);
          history.pushState({ searchParams: stateUrl.search.slice(1) }, '', nextUrl);

          // Volgende-link overnemen uit de opgehaalde sectie (of afronden op laatste pagina).
          const newButton = doc.querySelector('load-more-products [data-load-more-button]');
          if (newButton && newButton.getAttribute('href')) {
            this.button.setAttribute('href', newButton.getAttribute('href'));
          } else {
            this.button.remove();
            this.button = null;
            if (this.observer) this.observer.disconnect();
            this.removeSeoPagination();
          }

          this.updateProgress();

          // Toegankelijkheid: verplaats focus naar het eerste nieuwe product bij handmatig laden.
          if (manual && firstAppended) {
            const focusable = firstAppended.querySelector('a, button');
            if (focusable) {
              focusable.setAttribute('tabindex', '-1');
              focusable.focus();
            }
          }

          if (this.button) this.updateMode();

          if (typeof initializeScrollAnimationTrigger === 'function') {
            initializeScrollAnimationTrigger();
          }
        } catch (error) {
          // Bij een fout: knop weer tonen zodat de gebruiker het opnieuw kan proberen.
          if (this.button) {
            this.button.classList.remove('load-more__button--hidden');
            if (this.observer && this.sentinel) this.observer.unobserve(this.sentinel);
          }
          console.error('Load more products:', error);
        } finally {
          this.loading = false;
          this.setBusy(false);
        }
      }

      updateProgress() {
        const shown = this.renderedCount;
        if (this.statusShown) this.statusShown.textContent = String(shown);
        if (this.progressBar && this.total > 0) {
          const percent = Math.min(100, Math.round((shown / this.total) * 100));
          this.progressBar.style.width = `${percent}%`;
        }
      }

      setBusy(state) {
        if (this.spinner) this.spinner.hidden = !state;
        if (this.grid) this.grid.setAttribute('aria-busy', state ? 'true' : 'false');
        if (this.button) this.button.classList.toggle('load-more__button--loading', state);
      }

      removeSeoPagination() {
        const container = this.parentElement;
        const seo = container ? container.querySelector('[data-load-more-seo]') : null;
        if (seo) seo.remove();
      }
    }
  );
}
